import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { Loading } from '@liveauctioneers/hammer-ui-core/loading';
import { H1 } from '@liveauctioneers/hammer-ui-core/text';
import ms from 'ms';
import * as React from 'react';
import styled from 'styled-components';

import { ExternalRouteButton } from '@/components/RouteButton/RouteButton';
import { RouteLink } from '@/components/RouteLinks/RouteLinks';
import { Slider } from '@/components/Slider/Slider';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { EventContext } from '@/redux/modules/analytics';
import {
    getCatalogIsInstantAutopay,
    getCatalogSellerName,
    getCatalogTitle,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getCatalogRegistration } from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import { getItemIdsForCatalogWithMostBids } from '@/redux/modules/item/summary/itemSummary.selectors';
import {
    CompleteAccountModalContext,
    ModalContext,
    openLoginModal,
    openRegisterForAuctionModal,
} from '@/redux/modules/modal';
import { getCatalogUrl } from '@/utils/urls';

import WaitCard from './components/WaitCard/WaitCard';

type Props = {
    catalogId: number;
};

const WaitingForAuctionToStart = ({ catalogId }: Props) => {
    const dispatch = useAppDispatch();
    const { formatMessage } = useIntl();
    const isInstantAutopay = useAppSelector((state) => getCatalogIsInstantAutopay(state, catalogId));
    const name = useAppSelector((state) => getCatalogSellerName(state, catalogId));
    const title = useAppSelector((state) => getCatalogTitle(state, catalogId));
    const userLoggedIn = useAppSelector(isUserLoggedIn);

    const { blockedByAuctioneer, declined, registered, suspended } = useAppSelector((state) =>
        getCatalogRegistration(state, catalogId)
    );

    const itemIds = useAppSelector((state) => getItemIdsForCatalogWithMostBids(state, catalogId));

    const modalContext = React.useMemo<ModalContext>(
        () => ({
            catalogId,
            eventContext: EventContext.registerForAuction,
            id: catalogId,
            type: CompleteAccountModalContext.RegisterForAuction,
        }),
        [catalogId]
    );

    const registerForCatalog = () => {
        dispatch(
            openRegisterForAuctionModal({
                catalogId,
                fromBiddingConsole: true,
                isInstantAutopay,
                modalContext,
            })
        );
    };

    const loginModal = React.useCallback(() => dispatch(openLoginModal(modalContext)), [dispatch, modalContext]);

    const hasntRegistered = !registered || declined;

    return (
        <Container>
            <Header>
                <TitleText>
                    <FormattedMessage id="bidding_console.wait.title" />
                    <Loading />
                </TitleText>
                <StatusCTA>
                    <h2>
                        <FormattedMessage
                            id="bidding_console.wait.status_message"
                            values={{ name }}
                        />
                    </h2>
                    {!userLoggedIn && (
                        <Button onClick={loginModal}>
                            {formatMessage({
                                id: 'bidding_console.wait.login_button_text',
                            })}
                        </Button>
                    )}
                    {hasntRegistered && userLoggedIn && !(suspended || blockedByAuctioneer) && (
                        <Button onClick={registerForCatalog}>
                            {formatMessage({
                                id: 'bidding_console.wait.register_button_text',
                            })}
                        </Button>
                    )}
                    {userLoggedIn && (suspended || blockedByAuctioneer) && (
                        <ExternalRouteButton to="https://help.liveauctioneers.com/">
                            {formatMessage({ id: 'help_center' })}
                        </ExternalRouteButton>
                    )}
                </StatusCTA>
            </Header>
            <Body>
                <h2>
                    <FormattedMessage id="bidding_console.wait.image_slider_text" />
                </h2>
                <Cards>
                    <Slider autoScroll={ms('5s')}>
                        {itemIds.map((itemId) => (
                            <div key={`${itemId}-wait-card`}>
                                <WaitCard itemId={itemId} />
                            </div>
                        ))}
                    </Slider>
                </Cards>
            </Body>
            <HiddenSEOLinkContainer>
                <RouteLink to={getCatalogUrl(catalogId, title)}>
                    <FormattedMessage id="bidding_console.wait.hidden_text" />
                </RouteLink>
            </HiddenSEOLinkContainer>
        </Container>
    );
};

export default WaitingForAuctionToStart;

const Container = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 0;
    overflow: hidden;
`;

const Header = styled.header`
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    text-align: center;
    color: ${({ theme }) => theme.colors.white};
    transition: all 0.2s;
    box-shadow: 0 0 5px ${({ theme }) => theme.colors.shadow200};
    border-bottom: 1px solid ${({ theme }) => theme.colors.grey300};
    z-index: 100;
    background: ${({ theme }) => theme.colors.white};
`;

const TitleText = styled(H1)`
    transition: all 0.2s;
    border-bottom: 1px solid ${({ theme }) => theme.colors.grey300};
    width: calc(100% - 20px);
    padding: 32px;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobileWidth}) {
        padding: 16px;
    }
`;

const StatusCTA = styled.div`
    margin: 16px 0;
    color: ${({ theme }) => theme.colors.black};
    font-family: ${({ theme }) => theme.typography.fontPrimary};
    min-width: calc(100% - 20px);

    h2 {
        margin: 0;
    }

    button {
        width: calc(100% - 10px);
        margin: 16px auto 0;
        max-width: 350px;
    }
`;

const Body = styled.div`
    background: ${({ theme }) => theme.colors.grey500};
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    text-align: center;

    h2 {
        color: ${({ theme }) => theme.colors.black};
        margin: 32px 0 16px;
    }
`;

const Cards = styled.div`
    width: 100%;
    max-width: 450px;
    position: relative;
    margin: 0 16px 0;
    height: 305px;
    overflow: hidden;
`;

const HiddenSEOLinkContainer = styled.div`
    display: none;
`;
